import { cn } from '@/lib/utils'
import { Minus, Plus, Scan, User } from 'lucide-react'
import { RefObject, useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogFooter, DialogTitle, DialogTrigger } from '../ui/dialog'
import { Button } from '../ui/button'
import { useBookingParams } from '@/hooks/useBookingParams'
import { useTranslations } from 'next-intl'
import { buildRoomConfig } from '@/lib/helpers/parseRoomConfig'

type GuestType = 'rooms' | 'adults' | 'children'

const GuestSelector = ({
  rooms,
  adults,
  child,
  touched = false,
  triggerRef,
  disableUrlUpdate = false,
  onGuestsChange,
}: {
  rooms: string | null
  adults: string | null
  child: string | null
  touched?: boolean
  triggerRef: RefObject<HTMLButtonElement | null>
  disableUrlUpdate?: boolean
  onGuestsChange?: (guests: { adults: number; children: number; rooms: number }) => void
}) => {
  const [open, setOpen] = useState(false)
  const [guests, setGuests] = useState({
    rooms: Number(rooms) || 1,
    adults: Number(adults) || 1,
    children: Number(child) || 0,
  })
  const [error, setError] = useState<string | null>(null)
  const [hasBeenTouched, setHasBeenTouched] = useState(touched)

  useEffect(() => {
    setGuests({
      rooms: Number(rooms) || 1,
      adults: Number(adults) || 1,
      children: Number(child) || 0,
    })
    setHasBeenTouched(touched)
  }, [rooms, adults, child, touched])

  const t = useTranslations()

  const { updateParams } = useBookingParams()

  const updateGuestCount = (type: GuestType, increment: boolean) => {
    setError('')

    switch (type) {
      case 'rooms':
        setGuests((prev) => {
          if (prev[type] === 10 && increment) {
            setError(t('when_booking_more_than_six_rooms'))
            return prev
          }

          return {
            ...prev,
            rooms: increment ? Math.min(10, prev['rooms'] + 1) : Math.max(1, prev['rooms'] - 1),
          }
        })
        break
      case 'adults':
        setGuests((prev) => ({
          ...prev,
          adults: increment ? prev['adults'] + 1 : Math.max(1, prev['adults'] - 1),
        }))
        break

      default:
        setGuests((prev) => ({
          ...prev,
          [type]: increment ? prev[type] + 1 : Math.max(0, prev[type] - 1),
        }))
    }
  }

  const guestTypes: GuestType[] = ['rooms', 'adults', 'children']

  const handleSearch = () => {
    setOpen(false)
    setHasBeenTouched(true)

    if (disableUrlUpdate) {
      onGuestsChange?.(guests)
      return
    }

    updateParams((queryParams) => {
      queryParams.set('adult', guests.adults.toString())
      queryParams.set('child', guests.children.toString())
      queryParams.set('rooms', guests.rooms.toString())
      queryParams.set('roomConfig', buildRoomConfig(guests.adults, guests.children, guests.rooms))
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          ref={triggerRef}
          onClick={() => setOpen(true)}
          className='flex items-center hover:bg-background transition-colors md:shadow-md gap-2 bg-input border-secondary w-full border px-2 py-3 rounded-md'
        >
          <div
            className={cn('flex space-x-2 h-full w-1/2 items-center ', {
              'text-muted-foreground': !guests.rooms || !hasBeenTouched,
            })}
          >
            <Scan className='size-5' strokeWidth={1.5} />
            <p>{guests.rooms && hasBeenTouched ? guests.rooms : t('rooms')}</p>
          </div>

          <span className='w-[1px] h-full bg-border rounded-full' />

          <div
            className={cn('flex space-x-2 h-full w-1/2 items-center ', {
              'text-muted-foreground': !guests.adults || !hasBeenTouched,
            })}
          >
            <User className='size-5' strokeWidth={1.5} />
            <p>{guests.adults && hasBeenTouched ? guests.adults + guests.children : t('guests')}</p>
          </div>
        </button>
      </DialogTrigger>
      <DialogContent className='w-full rounded-lg p-0' showCloseButton={false}>
        <DialogTitle className='w-full pl-4 pt-4'>{t('add_guests')}</DialogTitle>
        <div className='relative px-4 space-y-4'>
          <div className='max-h-[calc(100vh_-_300px)] flex flex-col space-y-4'>
            {guestTypes.map((title) => (
              <div key={title} className='flex items-center justify-between'>
                <p className='text-lg capitalize'>{t(title)}</p>

                <div className='flex items-center space-x-4'>
                  <Button
                    onClick={() => updateGuestCount(title, false)}
                    variant='outline'
                    size='icon'
                    className='size-10 shrink-0'
                  >
                    <Minus />
                    <span className='sr-only'>{t('decrease')}</span>
                  </Button>

                  <div>{guests[title as keyof typeof guests]}</div>

                  <Button
                    onClick={() => updateGuestCount(title, true)}
                    variant='outline'
                    size='icon'
                    className='size-10 shrink-0'
                  >
                    <Plus />
                    <span className='sr-only'>{t('increase')}</span>
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {error && <p className='text-red-500'>{error}</p>}

          <DialogFooter className='py-4'>
            <Button
              className='w-full'
              onClick={() => {
                handleSearch()
              }}
            >
              {t('done')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default GuestSelector
